/**
 * WallOutletsList — Manage physical wall/floor network sockets
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  CheckIcon,
  ListBulletIcon,
  PlusIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import LocationTreeSelect from './common/LocationTreeSelect';
import { buildLocationMap, locationPath, locationPathOptions } from '../utils/locationTree';
import CiActivityPanel from './common/CiActivityPanel';
import ListPage from './common/ListPage';
import WallOutletsBulkDialog from './WallOutletsBulkDialog';
import WallOutletsGrid from './WallOutletsGrid';
import { OUTLET_TYPES, tipoEtiqueta } from '../config/outletTypes';
import '../styles/DetailView.css';
import DetailCrumb from './DetailCrumb';

function WallOutletsList({ companyId }) {
  const [outlets,      setOutlets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [filterLoc,    setFilterLoc]    = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [onlyFree,     setOnlyFree]     = useState(false);
  const [locations,    setLocations]    = useState([]);
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacion = useCallback(
    (item) => (item?.location_id ? locationPath(mapaUbicaciones, item.location_id) : '') || item?.location_name || '',
    [mapaUbicaciones],
  );
  const [showForm,     setShowForm]     = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [showBulk,     setShowBulk]     = useState(false);
  const [notice,       setNotice]       = useState('');

  useEffect(() => {
    if (companyId) {
      fetchOutlets();
      api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    }
  }, [companyId]);

  /**
   * `silencioso` no enciende el estado de carga: así `ListPage` no desmonta la
   * vista activa. Es lo que permite refrescar el listado tras un alta rápida
   * sin salir del modo pantalla completa ni perder lo que haya a medias.
   */
  const fetchOutlets = async ({ silencioso = false } = {}) => {
    try {
      if (!silencioso) setLoading(true);
      setError(null);
      const res = await api.getWallOutlets(companyId, { include_connection: 'true' });
      setOutlets(res.data || []);
    } catch (err) { setError(err.message); }
    finally { if (!silencioso) setLoading(false); }
  };

  const aviso = (texto) => {
    setNotice(texto);
    setTimeout(() => setNotice(''), 6000);
  };

  const conectadas = useMemo(() => outlets.filter(o => o.connection).length, [outlets]);

  const filtered = useMemo(() => {
    let list = outlets;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.code || '').toLowerCase().includes(q) ||
        (o.name || '').toLowerCase().includes(q) ||
        (o.room || '').toLowerCase().includes(q) ||
        (o.panel_name || '').toLowerCase().includes(q)
      );
    }
    if (filterLoc)    list = list.filter(o => String(o.location_id) === filterLoc);
    if (filterType)   list = list.filter(o => (o.outlet_type || 'rj45') === filterType);
    if (filterStatus) list = list.filter(o => (o.status || 'active') === filterStatus);
    if (onlyFree)     list = list.filter(o => !o.connection);
    return list;
  }, [outlets, search, filterLoc, filterType, filterStatus, onlyFree]);

  /** Texto de «conectada a»: el otro extremo del latiguillo. */
  const destino = (o) => {
    if (!o.connection) return null;
    const otro = o.connection.other_end || {};
    return `${otro.element_name || '—'}${otro.port_number != null ? ` P${otro.port_number}` : ''}`;
  };

  const columns = useMemo(() => [
    {
      key: 'code', label: 'Código', sortable: true, sortValue: (o) => o.code || '',
      render: (o) => <code className="list-code">{o.code}</code>,
    },
    { key: 'name', label: 'Nombre', sortable: true, sortValue: (o) => o.name || '' },
    {
      key: 'room', label: 'Sala / planta', sortable: true,
      sortValue: (o) => [o.room, o.floor].filter(Boolean).join(' · '),
      render: (o) => [o.room, o.floor].filter(Boolean).join(' · ') || '—',
    },
    { key: 'location_name', label: 'Sede', sortable: true, sortValue: (o) => rutaUbicacion(o), render: (o) => rutaUbicacion(o) || <span className="list-hint">—</span> },
    {
      key: 'outlet_type', label: 'Tipo', sortable: true,
      sortValue: (o) => tipoEtiqueta(o.outlet_type),
      render: (o) => <span className="list-badge">{tipoEtiqueta(o.outlet_type)}</span>,
    },
    {
      key: 'panel', label: 'Patch panel', sortable: true,
      sortValue: (o) => `${o.panel_name || ''} ${o.panel_port_label || ''}`,
      render: (o) => (o.panel_name ? (
        <span>
          {o.panel_name}
          {o.panel_port_label && <span className="list-hint"> · {o.panel_port_label}</span>}
        </span>
      ) : <span className="list-hint">—</span>),
    },
    {
      key: 'connection', label: 'Conectada a', sortable: true,
      sortValue: (o) => (o.connection ? 1 : 0),
      render: (o) => (o.connection
        ? <span className="list-badge list-badge--on">{destino(o)}</span>
        : <span className="list-hint">Libre</span>),
    },
    {
      key: 'status', label: 'Estado', sortable: true,
      sortValue: (o) => ((o.status || 'active') === 'active' ? 1 : 0),
      render: (o) => ((o.status || 'active') === 'active'
        ? <span className="list-badge list-badge--on">Activa</span>
        : <span className="list-badge list-badge--off">Inactiva</span>),
    },
  ], [rutaUbicacion]);

  const filters = [
    {
      key: 'location', label: 'Todas las sedes', allLabel: 'Todas las sedes',
      value: filterLoc, onChange: setFilterLoc,
      options: locationPathOptions(locations),
    },
    {
      key: 'type', label: 'Todos los tipos', allLabel: 'Todos los tipos',
      value: filterType, onChange: setFilterType,
      options: OUTLET_TYPES.map(t => ({ value: t.value, label: t.label })),
    },
    {
      key: 'status', label: 'Todos los estados', allLabel: 'Todos los estados',
      value: filterStatus, onChange: setFilterStatus,
      options: [{ value: 'active', label: 'Activa' }, { value: 'inactive', label: 'Inactiva' }],
    },
    {
      key: 'free', label: 'Solo libres', type: 'toggle',
      value: onlyFree, onChange: (v) => setOnlyFree(Boolean(v)), onValue: true,
      title: 'Enseñar solo las tomas sin latiguillo',
    },
  ];

  const exportRows = useMemo(() => filtered.map(o => ({
    'Código': o.code || '',
    'Nombre': o.name || '',
    'Sala': o.room || '',
    'Planta': o.floor || '',
    'Sede': rutaUbicacion(o),
    'Tipo': tipoEtiqueta(o.outlet_type),
    'Patch panel': o.panel_name || '',
    'Puerto del panel': o.panel_port_label || '',
    'Conectada a': destino(o) || '',
    'Latiguillo': o.connection?.cable_type || '',
    'Estado': (o.status || 'active') === 'active' ? 'Activa' : 'Inactiva',
  })), [filtered, rutaUbicacion]);

  const subtitulo = (() => {
    const total = outlets.length;
    const libres = total - conectadas;
    return [
      `${total} ${total === 1 ? 'toma' : 'tomas'}`,
      `${conectadas} ${conectadas === 1 ? 'conectada' : 'conectadas'}`,
      `${libres} ${libres === 1 ? 'libre' : 'libres'}`,
    ].join(' · ');
  })();

  if (!companyId) return <div className="wol-container"><div className="alert alert-warning">Selecciona una empresa.</div></div>;

  return (
    <>
      {showForm && (
        <WallOutletForm
          item={editItem}
          companyId={companyId}
          locations={locations}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSave={(creado) => {
            fetchOutlets();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) setEditItem(creado);
          }}
        />
      )}

      {showBulk && (
        <WallOutletsBulkDialog
          companyId={companyId}
          locations={locations}
          onClose={() => setShowBulk(false)}
          onCreated={(cuantas, cerrar) => {
            // Con fallos parciales el diálogo sigue abierto para corregirlos:
            // solo se refresca el listado y se avisa de lo que sí se creó.
            fetchOutlets();
            if (cuantas) aviso(`${cuantas} ${cuantas === 1 ? 'toma creada' : 'tomas creadas'}`);
            if (cerrar) setShowBulk(false);
          }}
        />
      )}
      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={BuildingOfficeIcon}
          title="Tomas de pared"
          subtitle={subtitulo}
          // Es una página propia (Cableado › Tomas de pared en el menú), así que
          // lleva cabecera de página normal: antes era una sección dentro de la
          // página «Cableado» con pestañas y por eso iba embebida.
          onCreate={() => { setEditItem(null); setShowForm(true); }}
          createLabel="Nueva toma"

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar código, nombre, sala o patch panel…"
          filters={filters}

          exportRows={exportRows}
          exportFileBase="tomas-de-pared"
          exportSheetName="Tomas de pared"

          columns={columns}
          columnStorageKey="cmdb_cols_wall_outlets"
          rows={filtered}
          rowKey={(o) => o.id}
          rowTitle={(o) => o.code}
          onRowClick={(o) => { setEditItem(o); setShowForm(true); }}

          headerActions={(
            <>
              {notice && <span className="list-badge list-badge--on">{notice}</span>}
              <button
                type="button"
                className="list-btn-secondary"
                onClick={() => setShowBulk(true)}
                title="Crear varias tomas de una vez en una tabla"
              >
                <RectangleStackIcon style={{ width: 15, height: 15 }} /> Crear en bloque
              </button>
            </>
          )}

          views={[
            { key: 'list', label: 'Lista', icon: ListBulletIcon, title: 'Tabla con todas las columnas' },
            {
              // Tabla editable: dar de alta una remesa sin salir del listado. Cada
              // fila nueva se copia de la anterior y adelanta código y puerto.
              key: 'grid', label: 'Alta rápida', icon: PlusIcon,
              title: 'Tabla editable: añade filas y se copian de la anterior',
              renderWhenEmpty: true,
              render: ({ rows }) => (
                <WallOutletsGrid
                  companyId={companyId}
                  locations={locations}
                  rows={rows}
                  defaultLocationId={filterLoc}
                  onCreated={(cuantas) => {
                    // Sin spinner: la tabla editable sigue montada (y en pantalla
                    // completa si lo estaba) y las tomas nuevas aparecen arriba.
                    fetchOutlets({ silencioso: true });
                    aviso(`${cuantas} ${cuantas === 1 ? 'toma creada' : 'tomas creadas'}`);
                  }}
                  onUpdated={(cuantas) => {
                    fetchOutlets({ silencioso: true });
                    aviso(`${cuantas} ${cuantas === 1 ? 'toma actualizada' : 'tomas actualizadas'}`);
                  }}
                  onOpenOutlet={(toma) => { setEditItem(toma); setShowForm(true); }}
                />
              ),
            },
            {
              key: 'card', label: 'Fichas', icon: Squares2X2Icon,
              title: 'Tarjetas con lo esencial de cada toma',
              render: ({ rows, rowActions }) => (
                <div className="list-cards">
                  {rows.map((o) => (
                    <div key={o.id} className="list-card">
                      <div className="list-card-head">
                        <div>
                          <div className="list-card-title list-code">{o.code}</div>
                          {o.name && <span className="list-hint">{o.name}</span>}
                        </div>
                        {(o.status || 'active') === 'active'
                          ? <span className="list-badge list-badge--on">Activa</span>
                          : <span className="list-badge list-badge--off">Inactiva</span>}
                      </div>
                      <div className="list-card-meta">
                        <span className="list-badge">{tipoEtiqueta(o.outlet_type)}</span>
                        {o.location_name && <span className="list-hint">{rutaUbicacion(o)}</span>}
                        {(o.room || o.floor) && (
                          <span className="list-hint">{[o.room, o.floor].filter(Boolean).join(' · ')}</span>
                        )}
                        {o.panel_name && (
                          <span className="list-hint">{o.panel_name}{o.panel_port_label ? ` · ${o.panel_port_label}` : ''}</span>
                        )}
                        {o.connection
                          ? <span className="list-badge list-badge--on">{destino(o)}</span>
                          : <span className="list-hint">Libre</span>}
                      </div>
                      <div className="list-card-actions">{rowActions(o)}</div>
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
          listKey="wall_outlets"
          // Se entra por la tabla editable: aquí casi siempre se viene a dar de
          // alta una remesa. Quien prefiera la tabla clásica cambia de vista y
          // su elección se recuerda.
          defaultView="grid"

          loading={loading}
          loadingText="Cargando tomas…"
          error={error}

          empty={{
            icon: '🏢',
            title: outlets.length === 0
              ? 'Todavía no hay tomas registradas'
              : 'Ninguna toma coincide con los filtros',
            subtitle: outlets.length === 0
              ? 'Da de alta una toma o crea una remesa entera desde «Crear en bloque».'
              : undefined,
            action: outlets.length === 0 ? (
              <>
                <button
                  type="button"
                  className="list-btn-secondary"
                  onClick={() => setShowBulk(true)}
                >
                  <RectangleStackIcon style={{ width: 14, height: 14 }} /> Crear en bloque
                </button>
                <button
                  type="button"
                  className="list-btn-primary"
                  onClick={() => { setEditItem(null); setShowForm(true); }}
                >
                  ＋ Nueva toma
                </button>
              </>
            ) : undefined,
          }}

          actions={{
            onEdit: (o) => { setEditItem(o); setShowForm(true); },
            deleteTitle: 'Eliminar toma',
            deleteMessage: (o) => (
              <>
                Se eliminará la toma <strong>{o.code}</strong>
                {o.connection ? ' y su conexión activa' : ''}.
              </>
            ),
            onDelete: async (o) => {
              await api.deleteWallOutlet(o.id, companyId);
              await fetchOutlets();
            },
          }}
        />
      </div>
    </>
  );
}

// ── Inline form ─────────────────────────────────────────────────────────────

function WallOutletForm({ item, companyId, locations, onClose, onSave }) {
  const isEdit = Boolean(item?.id);

  const [form, setForm] = useState({
    code:          item?.code          || '',
    name:          item?.name          || '',
    room:          item?.room          || '',
    floor:         item?.floor         || '',
    panel_code:    item?.panel_code    || '',
    panel_id:      item?.panel_id      ? String(item.panel_id)      : '',
    panel_port_id: item?.panel_port_id ? String(item.panel_port_id) : '',
    outlet_type:   item?.outlet_type   || 'rj45',
    notes:         item?.notes         || '',
    status:        item?.status        || 'active',
    location_id:   item?.location_id   ? String(item.location_id)   : '',
  });
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');
  const [isDirty,    setIsDirty]    = useState(false);
  const [activeTab,  setActiveTab]  = useState('general');
  const [panels,     setPanels]     = useState([]);
  const [panelPorts, setPanelPorts] = useState([]);

  // Load panels on mount
  useEffect(() => {
    if (!companyId) return;
    api.getElements(companyId, { element_type: 'panel' })
      .then(r => setPanels(r.data || []))
      .catch(() => {});
  }, [companyId]);

  // Load ports when panel changes
  useEffect(() => {
    if (!form.panel_id) { setPanelPorts([]); return; }
    api.getPanelPorts(form.panel_id, companyId)
      .then(r => setPanelPorts(r.data || []))
      .catch(() => setPanelPorts([]));
  }, [form.panel_id, companyId]); // eslint-disable-line

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const setField = (name, value) => {
    setForm(f => ({ ...f, [name]: value }));
    setIsDirty(true);
    setErr('');
  };

  const handleDiscard = () => {
    if (isEdit) {
      setForm({
        code: item?.code || '', name: item?.name || '', room: item?.room || '',
        floor: item?.floor || '', panel_code: item?.panel_code || '',
        panel_id:      item?.panel_id      ? String(item.panel_id)      : '',
        panel_port_id: item?.panel_port_id ? String(item.panel_port_id) : '',
        outlet_type: item?.outlet_type || 'rj45', notes: item?.notes || '',
        status: item?.status || 'active', location_id: item?.location_id ? String(item.location_id) : '',
      });
    } else { onClose(); }
    setIsDirty(false);
    setErr('');
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();
    if (!form.code.trim()) { setErr('El código es obligatorio.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        location_id:   form.location_id   ? Number(form.location_id)   : null,
        panel_id:      form.panel_id      ? Number(form.panel_id)      : null,
        panel_port_id: form.panel_port_id ? Number(form.panel_port_id) : null,
      };
      let creado = null;
      if (isEdit) await api.updateWallOutlet(item.id, payload, companyId);
      else        creado = registroGuardado(await api.createWallOutlet(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSave?.(creado);
    } catch (er) { setErr(er.message || 'Error al guardar'); }
    finally { setSaving(false); }
  };


  const statusStyle = form.status === 'active'
    ? { bg: '#dcfce7', color: '#15803d', label: 'Activa' }
    : { bg: '#fee2e2', color: '#991b1b', label: 'Inactiva' };

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon />
          Tomas de pared
        </button>
        <DetailCrumb label={form.code} />
        <div className="dv-topbar-spacer" />
        {err && <span style={{ color: '#dc2626', fontSize: '0.78rem' }}>⚠ {err}</span>}
        <div className="dv-topbar-actions">
          <span className="dv-status-badge" style={{ background: statusStyle.bg, color: statusStyle.color }}>
            ● {statusStyle.label}
          </span>
          {isDirty && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}
          <button
            className="dv-btn dv-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={saving || !isDirty}
          >
            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* ── SmartBar ── */}
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')}>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem' }}>{form.outlet_type || '—'}</span>
          <span className="dv-smart-btn-label">Tipo</span>
        </button>
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
          <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem' }}>
            {[form.room, form.floor].filter(Boolean).join(' · ') || '—'}
          </span>
          <span className="dv-smart-btn-label">Sala / Planta</span>
        </button>
        {(form.panel_id || form.panel_code) && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('general')}>
            <span className="dv-smart-btn-value" style={{ fontSize: '0.85rem' }}>
              {panels.find(p => String(p.id) === form.panel_id)?.name || form.panel_code}
            </span>
            <span className="dv-smart-btn-label">Panel</span>
          </button>
        )}
      </div>

      {/* ── Body ── */}
      <div className="dv-body">
        <form className="dv-main" onSubmit={handleSubmit}>
          <input
            className="dv-record-title"
            value={form.code}
            onChange={e => setField('code', e.target.value)}
            placeholder="Código de toma…"
            required
          />

          <div className="dv-tabs-nav">
            <button type="button" className={`dv-tab-btn${activeTab === 'general'   ? ' active' : ''}`} onClick={() => setActiveTab('general')}>Identificación</button>
            <button type="button" className={`dv-tab-btn${activeTab === 'ubicacion' ? ' active' : ''}`} onClick={() => setActiveTab('ubicacion')}>Ubicación</button>
          </div>

          {activeTab === 'general' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Datos de la toma</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Código <span className="text-danger">*</span></label>
                    <input className="form-control" value={form.code} onChange={e => setField('code', e.target.value)} placeholder="T-A-07" required />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Nombre</label>
                    <input className="form-control" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Toma pasillo A" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Patch Panel</label>
                    <select
                      className="form-select"
                      value={form.panel_id}
                      onChange={e => {
                        const pid = e.target.value;
                        const p   = panels.find(x => String(x.id) === pid);
                        setField('panel_id', pid);
                        setField('panel_code', p ? (p.code || p.name || '') : '');
                        setField('panel_port_id', '');
                      }}
                    >
                      <option value="">— sin panel —</option>
                      {panels.map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Puerto del panel</label>
                    <select
                      className="form-select"
                      value={form.panel_port_id}
                      disabled={!form.panel_id || panelPorts.length === 0}
                      onChange={e => setField('panel_port_id', e.target.value)}
                    >
                      <option value="">— sin puerto —</option>
                      {panelPorts.map(pp => (
                        <option key={pp.id} value={pp.id}>
                          {pp.label || `P${pp.port_number}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Tipo de toma</label>
                    <select className="form-select" value={form.outlet_type} onChange={e => setField('outlet_type', e.target.value)}>
                      {OUTLET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={form.status} onChange={e => setField('status', e.target.value)}>
                      <option value="active">Activa</option>
                      <option value="inactive">Inactiva</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ubicacion' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div className="dv-section-title">Ubicación física</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Sala</label>
                    <input className="form-control" value={form.room} onChange={e => setField('room', e.target.value)} placeholder="Sala de reuniones 4" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Planta</label>
                    <input className="form-control" value={form.floor} onChange={e => setField('floor', e.target.value)} placeholder="Planta 3" />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Ubicación</label>
                    <LocationTreeSelect
                      locations={locations}
                      value={form.location_id}
                      onChange={(id) => setField('location_id', id)}
                      placeholder="— sin ubicación —"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </form>

        {/* ── Right sidebar ── */}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="wall_outlet"
          ciId={item?.id}
          tipoLegible="la toma"
        />
      </div>
    </div>
  );
}

export default WallOutletsList;
